import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

export default class extends Controller {
  static get targets() { return ["list", "panel"]; }
  static get values() { return { url: String, togglePinUrl: String }; }
  static get classes() { return ["pinned"]; }

  // jQuery UI announces a finished drag through its own callback, which no addEventListener -
  // and so no data-action - can see, hence the sortable is set up here. The quick-nav clicks are
  // wired through data-action on the mount (see the views) so a fresh render of the list never
  // loses the binding.
  connect() {
    if (this.hasListTarget) {
      $(this.listTarget).sortable({
        // Only the .panel-heading child elements support dragging; omit this to make the
        // entire <li>...</li> draggable.
        handle: ".panel-heading",
        // Pinned rows can only move via the pin toggle, never by being dragged themselves.
        cancel: `.${this.pinnedClass}`,
        update: (event, ui) => this.handleReorder(ui.item[0]),
      });
    }
  }

  disconnect() {
    if (this.hasListTarget) { $(this.listTarget).sortable("destroy"); }
  }

  onQuickNavClick(event) {
    if (event.target.closest(".to-top")) {
      window.scrollTo(0, 0);
    } else if (event.target.closest(".to-bottom")) {
      window.scrollTo(0, document.body.scrollHeight);
    }
  }

  // A pinned item can never be dragged (see `cancel` above), so the only way an unpinned row can
  // end up above a pinned one is if the item that was just dragged landed there.
  handleReorder(movedElement) {
    const panels = this.panelTargets;
    const pinnedPanels = panels.filter(panel => panel.classList.contains(this.pinnedClass));
    const lastPinnedPanel = pinnedPanels[pinnedPanels.length - 1];

    const unpinnedHasInvalidOrder = panels.some((panel, index) => {
      const isPinned = panel.classList.contains(this.pinnedClass);
      return !isPinned && panels.slice(index + 1).some(later => later.classList.contains(this.pinnedClass));
    });

    if (unpinnedHasInvalidOrder) {
      lastPinnedPanel.insertAdjacentElement("afterend", movedElement);
    }

    this.persist(movedElement);
  }

  onKeyup(event) {
    if (event.key === "Enter") {
      this.moveToTypedPosition(event.currentTarget);
    } else if (event.key === "Escape") {
      this.resetInput(event.currentTarget);
    }
  }

  // Select text to make it easier for new input.
  selectText(event) {
    event.currentTarget.select();
  }

  onBlur(event) {
    this.resetInput(event.currentTarget);
  }

  moveToTypedPosition(input) {
    // At this point, index isn't strictly an index, but the value entered by the user. We first
    // check to see if it's a valid position number. If it is, we turn it into a proper 0-index.
    let index = parseInt(input.value);
    if (index < 1) { return; }
    index -= 1;

    const panels = this.panelTargets;
    const pinnedCount = panels.filter(panel => panel.classList.contains(this.pinnedClass)).length;

    // Adjust the index to account for pinned items.
    if (index <= pinnedCount) { index = pinnedCount; }

    // Ensure we don't overflow and simply use the last index.
    index = (index + 1 > panels.length) ? panels.length - 1 : index;

    const previousIndex = parseInt(input.dataset.previousIndex);
    const panelToMove = panels[previousIndex];
    const panelToDisplace = panels[index];

    // Determine where to insert the element being moved.
    if (previousIndex < index) {
      panelToDisplace.insertAdjacentElement("afterend", panelToMove);
    } else {
      panelToDisplace.insertAdjacentElement("beforebegin", panelToMove);
    }

    $(this.listTarget).sortable("refresh");
    $(this.listTarget).sortable("refreshPositions");

    this.persist(panelToMove);
  }

  resetInput(input) {
    const previousIndex = parseInt(input.dataset.previousIndex);
    input.setAttribute("value", previousIndex + 1);
    input.value = previousIndex + 1;
  }

  togglePin(event) {
    const button = event.currentTarget;
    const panel = button.closest(".panel");

    button.disabled = true;

    const body = new URLSearchParams();
    body.append("gallery_product_id", panel.dataset.modelId);

    const options = RequestHelper.defaultPostOptions();
    options.body = body;

    fetch(this.togglePinUrlValue, options).
      then(response => {
        if (!response.ok) { throw new Error(`Toggle pin failed (${response.status})`); }
        return response.json();
      }).
      then(({ pinned }) => {
        // Computed before this panel's own class is toggled below: when unpinning the last
        // pinned item this is the panel itself, and leaving it in place is already the right
        // "top of the unpinned group" position.
        const pinnedPanels = this.panelTargets.filter(p => p.classList.contains(this.pinnedClass));
        const lastPinnedPanel = pinnedPanels[pinnedPanels.length - 1];
        const message = `Item successfully ${pinned ? "pinned" : "unpinned"}`;

        panel.classList.toggle(this.pinnedClass, pinned);

        if (!lastPinnedPanel) {
          this.listTarget.prepend(panel);
        } else if (lastPinnedPanel !== panel) {
          lastPinnedPanel.insertAdjacentElement("afterend", panel);
        }

        this.persist(panel);
        window.ltAlert({ message: message, type: "toast-success", duration: 2500, lasso: false });
        button.disabled = false;
      }).
      catch(error => {
        button.disabled = false;
        console.warn("Request failed: ", error);
      });
  }

  persist(elementMoved) {
    const panels = this.panelTargets;
    let movedToIndex = 0;
    const body = new URLSearchParams();

    // Persist the new indices.
    panels.forEach((panel, index) => {
      body.append("order[]", panel.dataset.modelId);
      if (panel === elementMoved) { movedToIndex = index; }

      // Keep track of the previous index so we know where to move the new element when using
      // the text input method. Need to update both the value attribute and the DOM value since
      // we rely on the display and the element value.
      const input = panel.querySelector(".model-inline-position-editor");
      input.dataset.previousIndex = index;
      input.setAttribute("value", index + 1);
      input.value = index + 1;
    });

    const options = RequestHelper.defaultPostOptions();
    options.body = body;

    fetch(this.urlValue, options).
      then(response => {
        if (!response.ok) { throw new Error(`Order update failed (${response.status})`); }

        window.ltAlert({
          message: `Item successfully moved to position ${movedToIndex + 1}.`,
          type: "toast-success",
          duration: 2500,
          lasso: false,
        });
      }).
      catch(error => console.warn("Request failed: ", error));
  }
}
